import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { List, Tooltip, Button, Form, Input, Space, Typography, App, Popconfirm, Tag, Divider } from 'antd';
import { tc, getLocale } from '../../i18n/common';
import { LikeOutlined, PushpinOutlined, CheckCircleOutlined, DeleteOutlined } from '@ant-design/icons';

const { TextArea } = Input;
const { Text } = Typography;

const Comments = ({ videoId }) => {
    const [comments, setComments] = useState([]);
    const { message } = App.useApp();
    const [loading, setLoading] = useState(false);
    const [user, setUser] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();

    const isAdmin = useMemo(() => {
        const rr = (user?.azure_roles || []).map(r => String(r).toLowerCase());
        const orr = (user?.azure_organization_roles || []).map(r => String(r).toLowerCase());
        return rr.includes('admin') || orr.includes('admin');
    }, [user]);

    const load = async () => {
        setLoading(true);
        try {
            const [resUser, res] = await Promise.all([
                axios.post('/user').catch(() => ({ data: null })),
                axios.get(`/v1/videos/${videoId}/comments`)
            ]);
            setUser(resUser?.data || null);
            setComments(res.data?.data || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (videoId) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoId]);

    const handleSubmit = async (values) => {
        if (!values.body || !values.body.trim()) return;
        setSubmitting(true);
        try {
            console.log('[Questions] submit', { videoId, body: values.body });
            await axios.post(`/v1/videos/${videoId}/comments`, {
                body: values.body
            });
            form.resetFields();
            message.success(tc('questionSubmitted', getLocale()));
            await load();
        } catch (e) {
            message.error(e?.response?.data?.error || tc('submitFailed', getLocale()))
        } finally {
            setSubmitting(false);
        }
    };

    const toggleLike = async (id) => {
        try {
            const res = await axios.post(`/v1/comments/${id}/like`);
            // Optimistic update
            setComments(prev => prev.map(c => ({
                ...c,
                likes_count: c.id === id ? res.data?.likes_count ?? c.likes_count : c.likes_count,
                replies: c.replies
            })));
        } catch (e) {}
    };

    const pin = async (id) => {
        try {
            await axios.post(`/v1/comments/${id}/pin`);
            message.success('Pinned');
            await load();
        } catch (e) {
            message.error('Pin failed');
        }
    };

    const approve = async (id) => {
        try {
            await axios.post(`/v1/comments/${id}/approve`);
            message.success('Approved');
            await load();
        } catch (e) {
            message.error('Approve failed');
        }
    };

    const remove = async (id) => {
        try {
            await axios.delete(`/v1/comments/${id}`);
            message.success('Deleted');
            await load();
        } catch (e) {
            message.error('Delete failed');
        }
    };

    const renderActions = (item) => {
        return (
            <Space size="small">
                <Button size="small" type="text" icon={<LikeOutlined />} onClick={() => toggleLike(item.id)}>
                    {item.likes_count}
                </Button>
                {isAdmin && (
                    <>
                        {!item.is_pinned && (
                            <Button size="small" type="link" icon={<PushpinOutlined />} onClick={() => pin(item.id)}>Pin</Button>
                        )}
                        {item.status !== 'approved' && (
                            <Button size="small" type="link" icon={<CheckCircleOutlined />} onClick={() => approve(item.id)}>Approve</Button>
                        )}
                        <Popconfirm title="Delete comment?" onConfirm={() => remove(item.id)}>
                            <Button size="small" danger type="link" icon={<DeleteOutlined />}>Delete</Button>
                        </Popconfirm>
                    </>
                )}
            </Space>
        );
    };

    const renderComment = (item) => (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Text strong>{item.author}</Text>
                {item.is_pinned ? <Tag color="gold">Pinned</Tag> : null}
                {item.status !== 'approved' ? <Tag>Pending</Tag> : null}
                <Tooltip title={new Date(item.created_at).toLocaleString()}>
                    <Text type="secondary">{new Date(item.created_at).toLocaleDateString()}</Text>
                </Tooltip>
            </div>
            <div style={{ marginTop: 6 }}>{item.body}</div>
            <div style={{ marginTop: 6 }}>{renderActions(item)}</div>
            {(item.replies || []).length > 0 && (
                <div style={{ marginTop: 12, paddingLeft: 16, borderLeft: '2px solid #f0f0f0' }}>
                    {item.replies.map(r => (
                        <div key={r.id} style={{ marginBottom: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text strong>{r.author}</Text>
                                {r.status !== 'approved' ? <Tag>Pending</Tag> : null}
                                <Tooltip title={new Date(r.created_at).toLocaleString()}>
                                    <Text type="secondary">{new Date(r.created_at).toLocaleDateString()}</Text>
                                </Tooltip>
                            </div>
                            <div style={{ marginTop: 6 }}>{r.body}</div>
                        </div>
                    ))}
                </div>
            )}
            <Divider style={{ margin: '12px 0' }} />
        </div>
    );

    return (
        <div>
            <Form form={form} onFinish={handleSubmit} layout="vertical" style={{ marginTop: 16 }}>
                <Form.Item name="body" label={tc('addQuestion', getLocale())}>
                    <TextArea rows={4} placeholder={tc('questionPlaceholder', getLocale())} maxLength={5000} style={{ borderRadius: 8 }} />
                </Form.Item>
                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={submitting}>{tc('postQuestion', getLocale())}</Button>
                    <Text type="secondary" style={{ marginLeft: 8 }}>{tc('anonymousNote', getLocale())}</Text>
                </Form.Item>
            </Form>

            <List
                loading={loading}
                dataSource={comments}
                itemLayout="vertical"
                locale={{ emptyText: tc('noQuestionsYet', getLocale()) }}
                renderItem={(item) => (
                    <List.Item>
                        {renderComment(item)}
                    </List.Item>
                )}
            />
        </div>
    );
};

export default Comments;
